import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';

import { auth, db } from '@src/firebase/firebase';

interface UserOrganization {
  id: string;
  name: string;
  [key: string]: unknown;
}

interface OnboardingMap {
  [orgId: string]: boolean;
}

interface UseOrgOnboardingFilterResult {
  filteredOrganizations: UserOrganization[];
  loading: boolean;
}

const useOrgOnboardingFilter = (
  userOrganizations: UserOrganization[],
): UseOrgOnboardingFilterResult => {
  const [onboardingMap, setOnboardingMap] = useState<OnboardingMap>({});
  const [loading, setLoading] = useState(false);
  const uid = auth.currentUser?.uid;

  const orgIdsKey = userOrganizations
    .map(o => o.id)
    .sort()
    .join(',');

  useEffect(() => {
    if (!uid || !userOrganizations.length) return;

    setLoading(true);
    const refs = userOrganizations.map(org =>
      getDoc(doc(db, 'organizations', org.id, 'users', uid)),
    );

    Promise.all(refs).then(snapshots => {
      const map: OnboardingMap = {};
      snapshots.forEach(snap => {
        if (!snap.exists()) return;
        const data = snap.data();
        // Missing field → treat as true (pre-feature users)
        map[snap.ref.parent.parent!.id] = data?.onboardingCompleted !== false;
      });
      setOnboardingMap(map);
      setLoading(false);
    });
  }, [orgIdsKey, uid]);

  const filteredOrganizations = useMemo(
    () =>
      userOrganizations.filter(
        // Not yet in map → optimistic show; missing doc → true
        org => onboardingMap[org.id] !== false,
      ),
    [userOrganizations, onboardingMap],
  );

  return { filteredOrganizations, loading };
};

export default useOrgOnboardingFilter;
